// Cookie / tracking consent. Google Analytics and the ad scripts are only loaded when the visitor's choice allows it.
// Visitors in the EEA, UK and Switzerland must opt in first; elsewhere the scripts load as before and the banner stays hidden.
//
// Where a visitor is can't be known exactly from the browser, so this uses the time zone and language as a cautious
// signal: any European time zone or EEA/UK/CH region code counts as covered. Google's own "Privacy & messaging" consent
// message in AdSense (IP-based, TCF-certified) is the stronger long-term answer and works alongside this.

import { adsExcludedPath } from "@/lib/adPlacement";
import { isInternalTraffic } from "@/lib/internalTraffic";

export interface Consent { analytics: boolean; ads: boolean; ts: number }

const KEY = "fdp-consent-v1";
export const OPEN_CONSENT_EVENT = "fdp-open-consent";
export const GA_ID = "G-Q1H24JEZX6";
export const ADSENSE_CLIENT = "ca-pub-4226631265041590";

const COVERED_REGIONS = new Set(
  "AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE IS LI NO GB CH".split(" "),
);
const COVERED_ATLANTIC = new Set(["Atlantic/Azores", "Atlantic/Madeira", "Atlantic/Canary", "Atlantic/Faroe", "Atlantic/Reykjavik"]);

export function isRegulatedRegion(timeZone: string | undefined, languages: readonly string[]): boolean {
  if (timeZone && (timeZone.startsWith("Europe/") || COVERED_ATLANTIC.has(timeZone))) return true;
  return languages.some((l) => {
    const region = /^[a-z]{2,3}[-_]([A-Za-z]{2})\b/.exec(l)?.[1]?.toUpperCase();
    return !!region && COVERED_REGIONS.has(region);
  });
}

export function visitorIsRegulated(): boolean {
  try {
    return isRegulatedRegion(Intl.DateTimeFormat().resolvedOptions().timeZone, navigator.languages?.length ? navigator.languages : [navigator.language]);
  } catch { return true; } // can't tell: assume covered
}

// The choice is only a local "dismissed" flag plus the two toggles; nothing is sent to or stored on a server.
// Order of persistence: localStorage, then sessionStorage (e.g. localStorage blocked), then memory for this page view.
let memoryConsent: string | null = null;

function parseConsent(raw: string | null | undefined): Consent | null {
  try {
    if (!raw) return null;
    const j = JSON.parse(raw) as Partial<Consent>;
    return typeof j.analytics === "boolean" && typeof j.ads === "boolean" ? { analytics: j.analytics, ads: j.ads, ts: Number(j.ts) || 0 } : null;
  } catch { return null; }
}

type Reader = Pick<Storage, "getItem">;
type Writer = Pick<Storage, "setItem">;

export function readConsent(store?: Reader | null): Consent | null {
  if (store !== undefined) { try { return parseConsent(store?.getItem(KEY)); } catch { return null; } }
  for (const get of [() => window.localStorage, () => window.sessionStorage]) {
    try { const c = parseConsent(get().getItem(KEY)); if (c) return c; } catch { /* storage unavailable: try the next place */ }
  }
  return parseConsent(memoryConsent);
}

export function writeConsent(c: Omit<Consent, "ts">, store?: Writer | null, now = Date.now()): Consent {
  const full = { ...c, ts: now };
  const raw = JSON.stringify(full);
  if (store !== undefined) { try { store?.setItem(KEY, raw); } catch { /* ignore */ } return full; }
  memoryConsent = raw; // always kept, so the choice holds for the rest of this page view even with storage blocked
  let saved = false;
  try { window.localStorage.setItem(KEY, raw); saved = true; } catch { /* blocked or full */ }
  if (!saved) { try { window.sessionStorage.setItem(KEY, raw); } catch { /* memory only */ } }
  return full;
}

/** Whether a kind of script may load right now. */
export function allowed(kind: "analytics" | "ads", regulated: boolean, stored: Consent | null): boolean {
  return regulated ? !!stored?.[kind] : true;
}

type Gtag = (...a: unknown[]) => void;
const w = () => window as unknown as { dataLayer?: unknown[]; gtag?: Gtag };
const loaded = { analytics: false, ads: false };
let lastSignals: string | null = null;

// Ads are a free-tier feature: the app tells us once it knows the visitor's plan (and whether ads are switched on at all).
// Until then, and for Pro subscribers, the ad script is never loaded, so nothing can place ads (Auto ads included).
let planAllowsAds = false;
export function setPlanAllowsAds(v: boolean) { planAllowsAds = v; }

function addScript(src: string, attrs: Record<string, string> = {}) {
  const s = document.createElement("script");
  s.async = true; s.src = src;
  for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v);
  document.head.appendChild(s);
}

function setGoogleConsent(c: { analytics: boolean; ads: boolean }, kind: "default" | "update") {
  const win = w();
  win.dataLayer = win.dataLayer || [];
  if (!win.gtag) win.gtag = function () { (win.dataLayer as unknown[]).push(arguments); } as Gtag;
  win.gtag("consent", kind, {
    analytics_storage: c.analytics ? "granted" : "denied",
    ad_storage: c.ads ? "granted" : "denied",
    ad_user_data: c.ads ? "granted" : "denied",
    ad_personalization: c.ads ? "granted" : "denied",
  });
}

/** Loads whatever the choice allows. Safe to call repeatedly; each script loads once. */
export function loadAllowedScripts(regulated: boolean, stored: Consent | null) {
  if (typeof document === "undefined") return;
  const okA = allowed("analytics", regulated, stored), okAds = planAllowsAds && allowed("ads", regulated, stored);
  const key = `${okA}|${okAds}`;
  if (key !== lastSignals) {
    setGoogleConsent({ analytics: okA, ads: okAds }, lastSignals === null ? "default" : "update");
    lastSignals = key;
  }
  // Payment, sign-in, account and API-key pages never load the ad script. The check runs again on every in-app
  // navigation (ConsentBanner), so the script still loads once the visitor reaches a page where ads are allowed.
  const adsHere = okAds && !adsExcludedPath(window.location.pathname);
  if (okA && !loaded.analytics) {
    loaded.analytics = true;
    addScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
    w().gtag!("js", new Date());
    // Own devices and automated browsers are labelled up front so even the first page_view carries it (GA4 internal-traffic filter).
    w().gtag!("config", GA_ID, isInternalTraffic() ? { traffic_type: "internal" } : {});
  }
  if (adsHere && !loaded.ads) {
    loaded.ads = true;
    addScript(`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`, { crossorigin: "anonymous" });
  }
}

export function openConsentSettings() { window.dispatchEvent(new Event(OPEN_CONSENT_EVENT)); }
